
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/Sidebar";
import MobileBottomNav from "../../components/MobileBottomNav";

const ALLOWED_ROLES = ["user", "admin", "developer", "owner"];

export default function MaintenanceControlPage() {
  const { booted, user, role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canView = role === "owner" || role === "developer" || role === "admin";
  const canEdit = role === "owner" || role === "developer";

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);


  const [enabled, setEnabled] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");

  const [targetAt, setTargetAt] = useState("");
  const [savingTarget, setSavingTarget] = useState(false);
  const [targetError, setTargetError] = useState("");



  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [savingUserId, setSavingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");


  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logsError, setLogsError] = useState("");
  const [logsSearchTerm, setLogsSearchTerm] = useState("");

  const authToken = useMemo(() => {
    if (typeof window === "undefined") return "";
    try {
      return localStorage.getItem("authToken") || "";
    } catch {
      return "";
    }
  }, []);

  const isoToLocalInput = useCallback((iso) => {
    try {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        "T" +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
      );
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusError("");
    setTargetError("");
    try {
      const res = await fetch("/api/maintenance/status", {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setEnabled(!!data.enabled);
      setTargetAt(isoToLocalInput(data.until));
    } catch (e) {
      setStatusError("تعذر جلب حالة الصيانة.");
    } finally {
      setLoadingStatus(false);
    }
  }, [authToken, isoToLocalInput]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError("");
    try {
      const res = await fetch("/api/admin/roles/users", {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = (e?.message || "").includes("missing_token")
        ? "لازم تسجل دخول كتطوير/مالك عشان تشوف الرولات."
        : "تعذر جلب قائمة المستخدمين.";
      setUsersError(msg);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [authToken]);

  const loadLogs = useCallback(
    async (q = "") => {
      setLoadingLogs(true);
      setLogsError("");
      try {
        const qs = q ? `?q=${encodeURIComponent(q)}` : "";
        const res = await fetch(`/api/admin/roles/logs${qs}`, {
          cache: "no-store",
          headers: {
            Accept: "application/json",
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        });

        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || `HTTP ${res.status}`);
        }

        const data = await res.json();
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data.logs)
          ? data.logs
          : [];
        setLogs(arr);
      } catch (e) {
        setLogsError("تعذر جلب سجل تغييرات الرولات.");
        setLogs([]);
      } finally {
        setLoadingLogs(false);
      }
    },
    [authToken]
  );

  useEffect(() => {
    if (booted && user && canView) {
      loadStatus();
      loadUsers();
      loadLogs();
    }
  }, [booted, user, canView, loadStatus, loadUsers, loadLogs]);

  const toggleMaintenance = async () => {
    if (!canEdit || loadingStatus || savingStatus) return;
    setSavingStatus(true);
    setStatusError("");
    try {
      const res = await fetch("/api/maintenance/set", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ enabled: !enabled }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "فشل التحديث");
      }

      const data = await res.json();
      setEnabled(!!data.enabled);

      if (data.targetAt !== undefined) {
        setTargetAt(isoToLocalInput(data.until));
      }
    } catch (e) {
      const msg =
        (e && e.message && String(e.message).trim()) ||
        "ما قدرت أغيّر وضع الصيانة.";
      setStatusError(msg);
    } finally {
      setSavingStatus(false);
    }
  };

  const saveTargetAt = async () => {
    if (!canEdit || savingTarget) return;
    setSavingTarget(true);
    setTargetError("");

    try {
      const iso = targetAt ? new Date(targetAt).toISOString() : null;

      const res = await fetch("/api/maintenance/set", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enabled,
          until: iso,
        }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "فشل حفظ الموعد");
      }

      const data = await res.json();
      setTargetAt(isoToLocalInput(data.until || ""));
    } catch (e) {
      setTargetError("ما قدرت أحفظ موعد انتهاء الصيانة.");
    } finally {
      setSavingTarget(false);
    }
  };

  const updateUserRole = async (u, newRole) => {
    if (!canEdit) return;
    if (!u?.id || !ALLOWED_ROLES.includes(newRole)) return;

    setSavingUserId(u.id);
    setUsersError("");
    try {
      const res = await fetch(`/api/admin/roles/users/${u.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const updatedRole = data?.user?.role || newRole;

      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, role: updatedRole } : x))
      );


      loadLogs(logsSearchTerm);
    } catch (e) {
      setUsersError("فشل تغيير الرول. تأكد أنك owner/developer.");
    } finally {
      setSavingUserId(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const email = (u.email || "").toLowerCase();
      const name = (u.displayName || u.username || "").toLowerCase();
      const r = (u.role || "").toLowerCase();
      return email.includes(q) || name.includes(q) || r.includes(q);
    });
  }, [users, searchTerm]);

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    try {
      return d.toLocaleString();
    } catch {
      return d.toISOString();
    }
  };

  if (!booted) return null;
  if (!user) return <Navigate to="/" state={{ from: location }} replace />;
  if (!canView) return <Navigate to="/" replace />;

  return (
    <div
      className="min-h-screen text-white bg-transparent"
      style={{
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'row',
        background:
          "linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40,227,218,0.16) 0%, rgba(40,227,218,0) 100%), #081423",
      }}
    >
      {                     }
      <div className="hidden md:block sticky top-0 h-screen z-[200]">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      {                    }
      <div className="md:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      {                  }
      <div
        className="flex-1 flex flex-col md:ml-0"
        style={{ width: "100vw", marginLeft: isMobile ? "0" : "auto" }}
      >
        <div
          className="flex-1 p-6 pb-20 md:pb-6"
          style={{
            maxWidth: "100%",
            padding: isMobile ? '16px 12px 80px' : isTablet ? '20px 24px 24px' : '24px 48px 32px',
          }}
        >
          <div className="w-full max-w-5xl mx-auto">
            <nav style={{
              display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-evenly',
              flexWrap: 'wrap', gap: isMobile ? '6px' : '4px',
              padding: isMobile ? '10px 12px' : '0 16px',
              height: isMobile ? 'auto' : '52px',
              background: 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,.2) 0%,rgba(40,227,218,0) 100%),rgba(9,28,43,.6)',
              border: '1px solid rgba(40,227,218,.2)',
              borderRadius: '16px',
              backdropFilter: 'blur(4px)',
              marginBottom: '24px',
            }}>
              {[
                { label: 'نظرة عامة',           path: '/dashboard' },
                { label: 'إدارة المستخدمين',    path: '/user-management' },
                { label: 'سجل النشاط',          path: '/activity-log' },
                { label: 'الإبلاغات',           path: '/reports' },
                { label: 'إضافة مقرر / محاضر', path: '/add-course-lecturer' },
                { label: 'إدارة المقررات',      path: '/manage-courses-lecturers' },
                { label: 'التحكم بالصيانة',     active: true },
              ].map((item, i) => (
                <React.Fragment key={item.label}>
                  {i > 0 && !isMobile && <span style={{ color: 'rgba(255,255,255,.15)', fontSize: '18px', userSelect: 'none' }}>|</span>}
                  <span
                    onClick={() => item.path && navigate(item.path)}
                    style={{
                      fontFamily: 'Alexandria,sans-serif', fontSize: isMobile ? '12px' : '14px',
                      fontWeight: item.active ? 800 : 500, color: item.active ? '#7DF9E8' : '#94a3b8',
                      cursor: item.path ? 'pointer' : 'default',
                      padding: isMobile ? '5px 10px' : '0 4px', borderRadius: isMobile ? '8px' : 0,
                      background: item.active && isMobile ? 'rgba(40,227,218,.1)' : 'transparent',
                      transition: 'color .15s', whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { if (item.path) e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { if (item.path) e.currentTarget.style.color = '#94a3b8'; }}
                  >{item.label}</span>
                </React.Fragment>
              ))}
            </nav>

            {                     }
            <div
              className="mb-8 p-4 md:p-6"
              style={{
                direction: "rtl",
                textAlign: "right",
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                border: '1px solid rgba(40,227,218,.22)',
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="text-base md:text-lg font-semibold">
                    وضع الصيانة
                  </div>
                  <div className="text-xs md:text-sm opacity-80 mt-1">
                    {loadingStatus
                      ? "جاري التحميل..."
                      : enabled
                      ? "مفعّل (الموقع مقفول لغير المطورين)"
                      : "مطفي (الموقع شغال)"}
                  </div>

                  {!canEdit && (
                    <div className="text-[11px] text-white/60 mt-2">
                      ملاحظة: رولك يسمح بالمشاهدة فقط.
                    </div>
                  )}
                </div>

                <button
                  onClick={toggleMaintenance}
                  disabled={!canEdit || savingStatus || loadingStatus}
                  className="px-4 md:px-5 py-2 rounded-xl font-semibold transition whitespace-nowrap text-sm md:text-base"
                  style={{
                    background: enabled ? "#DC2626" : "#10B981",
                    opacity:
                      !canEdit || savingStatus || loadingStatus ? 0.6 : 1,
                  }}
                >
                  {savingStatus
                    ? "جاري التغيير..."
                    : enabled
                    ? "إطفاء الصيانة"
                    : "تفعيل الصيانة"}
                </button>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label className="text-xs md:text-sm opacity-90">
                  موعد انتهاء الصيانة (تاريخ + وقت):
                </label>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                  <input
                    type="datetime-local"
                    value={targetAt}
                    onChange={(e) => setTargetAt(e.target.value)}
                    className="px-3 py-2 rounded-xl text-xs md:text-sm bg-white/5 border border-white/10 outline-none"
                    style={{ direction: "rtl", minWidth: 220 }}
                    disabled={!canEdit || loadingStatus}
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={saveTargetAt}
                      disabled={!canEdit || savingTarget || loadingStatus}
                      className="px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold bg-white/10 hover:bg-white/15 transition"
                      style={{
                        opacity:
                          !canEdit || savingTarget || loadingStatus ? 0.6 : 1,
                      }}
                    >
                      {savingTarget ? "جاري الحفظ..." : "حفظ الموعد"}
                    </button>

                    <button
                      onClick={() => setTargetAt("")}
                      disabled={!canEdit || savingTarget || loadingStatus}
                      className="px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold bg-white/5 hover:bg-white/10 transition"
                    >
                      مسح الموعد
                    </button>
                  </div>
                </div>

                {targetError && (
                  <div className="text-xs md:text-sm text-red-400">
                    {targetError}
                  </div>
                )}

                {!!targetAt && (
                  <div className="text-[11px] md:text-xs text-white/60">
                    الموعد الحالي: {targetAt.replace("T", " ")}
                  </div>
                )}
              </div>

              {statusError && (
                <div className="mt-4 text-xs md:text-sm text-red-400">
                  {statusError}
                </div>
              )}
            </div>

            {                       }
            <div
              className="p-4 md:p-6"
              style={{
                direction: "rtl",
                textAlign: "right",
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                border: '1px solid rgba(40,227,218,.22)',
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-base md:text-lg font-semibold">
                    إدارة رولات المستخدمين
                  </div>
                  <div className="text-[11px] md:text-xs opacity-80 mt-1">
                    غيّر رول أي مستخدم إلى: user / admin / developer / owner
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="ابحث بالإيميل أو الاسم أو الرول..."
                    className="px-3 py-2 rounded-xl text-xs md:text-sm bg-white/5 border border-white/10 outline-none w-full sm:w-56"
                    style={{ direction: "rtl" }}
                  />
                  <button
                    onClick={loadUsers}
                    className="px-3 py-2 rounded-xl text-xs md:text-sm bg-white/10 hover:bg-white/15 transition whitespace-nowrap"
                  >
                    تحديث
                  </button>
                </div>
              </div>

              {usersError && (
                <div className="mb-3 text-xs md:text-sm text-red-400">
                  {usersError}
                </div>
              )}

              {loadingUsers ? (
                <div className="text-xs md:text-sm opacity-80">
                  جاري تحميل المستخدمين...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-xs md:text-sm opacity-80">
                  ما فيه بيانات.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs md:text-sm">
                    <thead className="text-white/70">
                      <tr>
                        <th className="py-2 px-2 text-right">الاسم</th>
                        <th className="py-2 px-2 text-right">البريد</th>
                        <th className="py-2 px-2 text-right">الرول الحالي</th>
                        <th className="py-2 px-2 text-right">تغيير الرول</th>
                        <th className="py-2 px-2 text-right">إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr
                          key={u.id}
                          className="border-t border-white/10"
                        >
                          <td className="py-2 px-2">
                            {u.displayName || u.username || "—"}
                          </td>
                          <td className="py-2 px-2 break-all">
                            {u.email || "—"}
                          </td>
                          <td className="py-2 px-2 font-semibold">
                            {u.role || "user"}
                          </td>
                          <td className="py-2 px-2">
                            <select
                              defaultValue={u.role || "user"}
                              disabled={!canEdit || savingUserId === u.id}
                              onChange={(e) =>
                                updateUserRole(u, e.target.value)
                              }
                              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 outline-none text-xs md:text-sm"
                            >
                              {ALLOWED_ROLES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 px-2">
                            {savingUserId === u.id ? (
                              <span className="text-[11px] md:text-xs opacity-80">
                                جاري الحفظ...
                              </span>
                            ) : (
                              <span className="text-[11px] md:text-xs opacity-60">
                                {canEdit ? "جاهز" : "عرض فقط"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {                             }
            <div
              className="p-4 md:p-6 mt-8"
              style={{
                direction: "rtl",
                textAlign: "right",
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                border: '1px solid rgba(40,227,218,.22)',
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-base md:text-lg font-semibold">
                    سجل تغييرات الرولات
                  </div>
                  <div className="text-[11px] md:text-xs opacity-80 mt-1">
                    سجلات اللوق محفوظة في قاعدة البيانات بشكل دائم،
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <input
                    value={logsSearchTerm}
                    onChange={(e) => setLogsSearchTerm(e.target.value)}
                    placeholder="ابحث بالبريد أو الاسم أو الرول..."
                    className="px-3 py-2 rounded-xl text-xs md:text-sm bg-white/5 border border-white/10 outline-none w-full sm:w-56"
                    style={{ direction: "rtl" }}
                  />
                  <button
                    onClick={() => loadLogs(logsSearchTerm)}
                    className="px-3 py-2 rounded-xl text-xs md:text-sm bg-white/10 hover:bg-white/15 transition whitespace-nowrap"
                  >
                    بحث
                  </button>
                </div>
              </div>

              {logsError && (
                <div className="mb-3 text-xs md:text-sm text-red-400">
                  {logsError}
                </div>
              )}

              {loadingLogs ? (
                <div className="text-xs md:text-sm opacity-80">
                  جاري تحميل سجل تغييرات الرولات...
                </div>
              ) : !logs.length ? (
                <div className="text-xs md:text-sm opacity-80">
                  ما فيه سجلات حتى الآن.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs md:text-sm">
                    <thead className="text-white/70">
                      <tr>
                        <th className="py-2 px-2 text-right">التاريخ</th>
                        <th className="py-2 px-2 text-right">
                          المستخدم المستهدف
                        </th>
                        <th className="py-2 px-2 text-right">من → إلى</th>
                        <th className="py-2 px-2 text-right">المنفّذ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr
                          key={log.id || `${log.targetUserId}-${log.createdAt}`}
                          className="border-t border-white/10"
                        >
                          <td className="py-2 px-2 whitespace-nowrap">
                            {formatDateTime(log.createdAt)}
                          </td>
                          <td className="py-2 px-2">
                            <div>
                              {log.targetDisplayName ||
                                log.targetUsername ||
                                log.targetEmail ||
                                "—"}
                            </div>
                            {(log.targetEmail || log.targetUsername) && (
                              <div className="text-[11px] md:text-xs opacity-70 mt-0.5">
                                {log.targetEmail || log.targetUsername}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 whitespace-nowrap">
                            {(log.oldRole || "—") +
                              " → " +
                              (log.newRole || "—")}
                          </td>
                          <td className="py-2 px-2">
                            <div>
                              {log.actorDisplayName ||
                                log.actorUsername ||
                                log.actorEmail ||
                                "—"}
                            </div>
                            {(log.actorEmail || log.actorUsername) && (
                              <div className="text-[11px] md:text-xs opacity-70 mt-0.5">
                                {log.actorEmail || log.actorUsername}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
        {                                            }
        <div className="flex flex-col items-center gap-4 mt-8 mb-6 w-full">
          {                                                        }
            <button
              onClick={() => navigate('/dashboard')}
              className="transition-all duration-200 w-full max-w-xs"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 36px',
                gap: '8px',
                background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                borderRadius: '14px',
                border: 'none',
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: '700',
                fontSize: '15px',
                lineHeight: '100%',
                textAlign: 'center',
                color: '#081423',
                cursor: 'pointer',
                boxShadow: '0 0 18px rgba(40,227,218,.4)'
              }}
            >
              لوحة تحكم الادمن
            </button>


            <button
              onClick={() => navigate('/maintenance-control')}
              className="transition-all duration-200 w-full max-w-xs"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 36px',
                gap: '8px',
                background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                borderRadius: '14px',
                border: 'none',
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: '700',
                fontSize: '15px',
                lineHeight: '100%',
                textAlign: 'center',
                color: '#081423',
                cursor: 'pointer',
                boxShadow: '0 0 18px rgba(40,227,218,.4)'
              }}
            >
              لوحة تحكم المالك
            </button>


        </div>

          </div>
        </div>

        <MobileBottomNav />
      </div>
    </div>
  );
}
